import {
    ARROW_DIRECTION_LABELS,
    ARROW_VARIANT_LABELS,
    type ArrowDirection,
    type ArrowVariant
} from '../../lib/lvs-arrow'
import {LVS_COLORS} from '../../lib/lvs-colors'
import {LVS_H_OPTIONS} from '../../lib/lvs-heights'
import {MonoLabel} from '../ui/mono-label'
import {UnderlineInput, UnderlineSelect} from '../ui/underline-field'

const DIRECTIONS: readonly ArrowDirection[] = ['left', 'right']
const VARIANTS: readonly ArrowVariant[] = ['compound', 'simple']

export type ArrowFillKey = 'white' | 'black'
const FILL_KEYS: readonly ArrowFillKey[] = ['white', 'black']
const FILL_LABELS: Readonly<Record<ArrowFillKey, string>> = {
    white: `Balts (${LVS_COLORS.white.ral})`,
    black: `Melns (${LVS_COLORS.black.ral})`
}

export interface ArrowControlsProps {
    readonly direction: ArrowDirection
    readonly onDirectionChange: (d: ArrowDirection) => void
    readonly h: number
    readonly onHChange: (h: number) => void
    readonly variant: ArrowVariant
    readonly onVariantChange: (v: ArrowVariant) => void
    readonly stemA: number
    readonly onStemAChange: (s: number) => void
    readonly fill: ArrowFillKey
    readonly onFillChange: (c: ArrowFillKey) => void
}

/**
 * Controlled inputs for the arrow geometry parameters: direction (705
 * supports left + right horizontals in phase 1), reference height h
 * (drives the A unit), variant (compound/simple), and stem length in
 * A-units.
 */
export function ArrowControls({
    direction,
    onDirectionChange,
    h,
    onHChange,
    variant,
    onVariantChange,
    stemA,
    onStemAChange,
    fill,
    onFillChange
}: ArrowControlsProps) {
    return (
        <div className='flex flex-wrap gap-x-8 gap-y-5 items-end'>
            <div className='flex flex-col gap-1.5'>
                <MonoLabel>Virziens</MonoLabel>
                <UnderlineSelect
                    value={direction}
                    onChange={e => onDirectionChange(e.target.value as ArrowDirection)}
                >
                    {DIRECTIONS.map(d => (
                        <option
                            key={d}
                            value={d}
                        >
                            {ARROW_DIRECTION_LABELS[d]}
                        </option>
                    ))}
                </UnderlineSelect>
            </div>

            <div className='flex flex-col gap-1.5'>
                <MonoLabel>Atskaites augstums h</MonoLabel>
                <UnderlineSelect
                    value={h}
                    onChange={e => onHChange(Number(e.target.value))}
                >
                    {LVS_H_OPTIONS.map(opt => (
                        <option
                            key={opt}
                            value={opt}
                        >{`h = ${opt} mm`}</option>
                    ))}
                </UnderlineSelect>
            </div>

            <div className='flex flex-col gap-1.5'>
                <MonoLabel>Variants</MonoLabel>
                <UnderlineSelect
                    value={variant}
                    onChange={e => onVariantChange(e.target.value as ArrowVariant)}
                >
                    {VARIANTS.map(v => (
                        <option
                            key={v}
                            value={v}
                        >
                            {ARROW_VARIANT_LABELS[v]}
                        </option>
                    ))}
                </UnderlineSelect>
            </div>

            <div className='flex flex-col gap-1.5'>
                <MonoLabel>Stieņa garums (A vienībās)</MonoLabel>
                <UnderlineInput
                    type='number'
                    min={0}
                    step={0.5}
                    value={stemA}
                    onChange={e => onStemAChange(Number(e.target.value))}
                    className='w-32'
                />
            </div>

            <div className='flex flex-col gap-1.5'>
                <MonoLabel>Krāsa</MonoLabel>
                <UnderlineSelect
                    value={fill}
                    onChange={e => onFillChange(e.target.value as ArrowFillKey)}
                >
                    {FILL_KEYS.map(k => (
                        <option
                            key={k}
                            value={k}
                        >
                            {FILL_LABELS[k]}
                        </option>
                    ))}
                </UnderlineSelect>
            </div>
        </div>
    )
}
